import axios from "axios";
import env from "../env";
import {
  IN_APP_NOTIFICATION_API_BASE_PATH,
  IN_APP_NOTIFICATION_VALIDATIONS,
} from "../constants/inAppNotificationConstant";
import {
  extractTargetUserCountFromItem,
  isUserSpecificNotification,
  isValidInAppNotificationListItem,
  mapApiListItem,
  normalizeTargetTypeFromApi,
  parseTargetUserCount,
} from "../utils/inAppNotificationUtils";
const IN_APP_NOTIFICATION_LIST_MAX_PAGE_SIZE =
  IN_APP_NOTIFICATION_VALIDATIONS.listMaxPageSize;
const targetUserCountCache = new Map();

export const seedNotificationTargetUserCounts = (items = []) => {
  items.forEach((item) => {
    if (!item?.id) return;
    const count = extractTargetUserCountFromItem(item);
    if (count != null) {
      targetUserCountCache.set(String(item.id), count);
    }
  });
};

export const getCachedTargetUserCount = (notificationId) => {
  if (!notificationId) return undefined;
  return targetUserCountCache.get(String(notificationId));
};

export const getDisplayTargetUserCount = (
  notificationId,
  _notification,
  fallbackTargetUserCount,
) => {
  const fallback = parseTargetUserCount(fallbackTargetUserCount);
  if (fallback != null) return fallback;

  return getCachedTargetUserCount(notificationId);
};

const findNotificationListItem = (items, notificationId) =>
  items.find(
    (item) =>
      String(item.id) === String(notificationId) ||
      String(item.notificationId) === String(notificationId),
  );
export const mapInAppNotificationDetailForUi = (data) => {
  if (!data || typeof data !== "object") {
    return mapApiListItem(data);
  }

  const record = data;
  const hasRootIdentity = Boolean(record.notificationId || record.id || record.title);

  const raw = hasRootIdentity
    ? record
    : record.notification || record.data || record;

  return mapApiListItem(raw);
};

export const resolveNotificationTargetUserCount = async (
  notification,
  fallbackTargetUserCount,
) => {
  if (!notification?.id || !isUserSpecificNotification(notification.targetType)) {
    return notification;
  }

  const resolvedCount = getDisplayTargetUserCount(
    notification.id,
    notification,
    fallbackTargetUserCount,
  );

  if (resolvedCount != null) {
    return { ...notification, targetUserCount: resolvedCount };
  }

  try {
    const items = await fetchAllInAppNotifications({ includeExpired: true });
    const listItem = findNotificationListItem(items, notification.id);
    const listCount = extractTargetUserCountFromItem(listItem);
    if (listCount != null) {
      return { ...notification, targetUserCount: listCount };
    }
  } catch {
  }

  return notification;
};

const getInAppNotificationUrl = (notificationId, subPath) => {
  const baseUrl = `${env.GET_DEVICEMANAGEMENT_API_URL()}${IN_APP_NOTIFICATION_API_BASE_PATH}`;
  if (notificationId && subPath === "configure") {
    return `${baseUrl}/${encodeURIComponent(notificationId)}/configure`;
  }
  if (notificationId) {
    return `${baseUrl}/${encodeURIComponent(notificationId)}`;
  }
  return baseUrl;
};

const jsonConfig = (params) => ({
  headers: {
    "Content-Type": "application/json",
  },
  ...(params ? { params } : {}),
});

export const getInAppNotificationErrorMessage = (
  error,
  fallback = "Request failed",
) => error?.response?.data?.message || error?.message || fallback;

const getInAppNotificationTargetTypeLabel = (targetType) =>
  normalizeTargetTypeFromApi(targetType) === "user-specific"
    ? "User specific"
    : "Broadcast";

export const getInAppNotificationSaveSuccessMessage = (
  isEditMode = false,
  targetType = "broadcast",
) => {
  const typeLabel = getInAppNotificationTargetTypeLabel(targetType);
  return isEditMode
    ? `${typeLabel} Notification updated successfully`
    : `${typeLabel} Notification added successfully`;
};

export const extractNotificationId = (data, fallback = "") => {
  if (!data) {
    return fallback;
  }
  if (typeof data === "string") {
    return data;
  }
  return data.notificationId || data.id || fallback;
};

export const parseInAppNotificationList = (data, requestedSize) => {
  if (Array.isArray(data)) {
    const items = data.map(mapApiListItem);
    return {
      items,
      isLastPage: items.length < requestedSize,
      totalElements: items.length,
    };
  }

  const rawItems =
    data?.content ??
    data?.items ??
    data?.data ??
    data?.notifications ??
    data?.notificationList ??
    data?.results ??
    [];

  const items = Array.isArray(rawItems) ? rawItems.map(mapApiListItem) : [];

  let isLastPage;
  if (typeof data?.last === "boolean") {
    isLastPage = data.last;
  } else if (data?.totalPages != null && data?.number != null) {
    isLastPage = data.number >= data.totalPages - 1;
  } else if (data?.page != null && data?.totalPages != null) {
    isLastPage = data.page >= data.totalPages;
  } else if (typeof data?.hasNext === "boolean") {
    isLastPage = !data.hasNext;
  } else {
    isLastPage = items.length < requestedSize;
  }

  return {
    items,
    isLastPage,
    totalElements: data?.totalElements ?? data?.total ?? data?.totalCount,
  };
};

export const getInAppNotifications = ({
  page = 0,
  size = 20,
  includeExpired = true,
} = {}) => {
  const cappedSize = Math.min(
    Math.max(1, Number(size) || 20),
    IN_APP_NOTIFICATION_LIST_MAX_PAGE_SIZE,
  );

  const params = {
    page: page + 1,
    size: cappedSize,
    includeExpired: Boolean(includeExpired),
  };

  return axios.get(getInAppNotificationUrl(), jsonConfig(params));
};

const IN_APP_NOTIFICATION_FETCH_PAGE_SIZE = 10;

export const fetchAllInAppNotifications = async ({
  includeExpired = true,
  pageSize = IN_APP_NOTIFICATION_FETCH_PAGE_SIZE,
} = {}) => {
  const cappedSize = Math.min(
    Math.max(1, Number(pageSize) || IN_APP_NOTIFICATION_FETCH_PAGE_SIZE),
    IN_APP_NOTIFICATION_LIST_MAX_PAGE_SIZE,
  );
  const allItems = [];
  const seenIds = new Set();
  let page = 0;
  let hasMore = true;

  while (hasMore && page < 100) {
    let response;
    try {
      response = await getInAppNotifications({
        page,
        size: cappedSize,
        includeExpired,
      });
    } catch (error) {
      if (allItems.length === 0) {
        throw error;
      }
      break;
    }

    const parsed = parseInAppNotificationList(response?.data, cappedSize);
    const pageItems = parsed.items.filter((item) => {
      if (!isValidInAppNotificationListItem(item)) {
        return false;
      }
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });

    allItems.push(...pageItems);

    if (parsed.items.length === 0) {
      hasMore = false;
    } else if (parsed.items.length < cappedSize) {
      hasMore = false;
    } else {
      hasMore = true;
    }

    page += 1;
  }

  seedNotificationTargetUserCounts(allItems);
  return allItems;
};

export const createInAppNotification = (body) =>
  axios.post(getInAppNotificationUrl(), body, jsonConfig());

export const updateInAppNotification = (id, body) =>
  axios.put(getInAppNotificationUrl(id), body, jsonConfig());

export const getInAppNotificationById = (id) =>
  axios.get(getInAppNotificationUrl(id), jsonConfig());

export const deleteInAppNotification = (id) =>
  axios.delete(getInAppNotificationUrl(id), jsonConfig());

export const configureInAppNotification = (notificationId, body) =>
  axios.put(getInAppNotificationUrl(notificationId, "configure"), body, jsonConfig());
